import { useRef, useMemo, useEffect, useState } from "react";
import { useControls } from "leva";
import { debounce } from "lodash";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { Noise } from "@react-three/postprocessing";
import { Scanline } from "@react-three/postprocessing";
import { HueSaturation } from "@react-three/postprocessing";

import { BlendFunction } from "postprocessing";
// 3D
import * as THREE from "three";
import { PointLightHelper } from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { useHelper, Html, CloudInstance, useProgress } from "@react-three/drei";
import CustomShaderMaterial from "three-custom-shader-material";
import vertexShader from "./shaders/vertex.glsl";
import vertexShader2 from "./shaders/vertex2.glsl";
import fragmentShader2 from "./shaders/fragment2.glsl";

import bgShader from "./shaders/bgShader.glsl";
import lineShader from "./shaders/lineShader.glsl";
import shaderOnText from "./shaders/shaderOnText.glsl";
// import shaderOnText from "./shaders/textShaderTwo.glsl";

import bgInteractive from "./shaders/bgInteractive.glsl";
// import bgInteractive from "./shaders/bgInteractive2.glsl";

import html2canvas from "html2canvas";
import gsap from "gsap";
import SplitType from "split-type";

const useDomToCanvas = (domEl) => {
  const [texture, setTexture] = useState();
  useEffect(() => {
    if (!domEl) return;
    const convertDomToCanvas = async () => {
      const canvas = await html2canvas(domEl, { backgroundColor: null });
      setTexture(new THREE.CanvasTexture(canvas));
    };

    convertDomToCanvas();

    const debouncedResize = debounce(() => {
      convertDomToCanvas();
    }, 100);

    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
    };
  }, [domEl]);

  return texture;
};

function Lights() {
  const pointLightRef = useRef();

  useHelper(pointLightRef, PointLightHelper, 0.7, "cyan");

  const config = useControls("Lights", {
    color: "white",
    intensity: { value: 20, min: 0, max: 5000, step: 0.01 },
    distance: { value: 12, min: 0, max: 100, step: 0.1 },
    decay: { value: 1, min: 0, max: 5, step: 0.1 },
    position: { value: [0, 0, 7] },
  });
  return <pointLight ref={pointLightRef} {...config} />;
}

const Scene = ({ fragShader }) => {
  const state = useThree();
  const { width, height } = state.viewport;
  const [domEl, setDomEl] = useState(null);

  const materialRef = useRef();
  const textureDOM = useDomToCanvas(domEl);

  const uniforms = useMemo(
    () => ({
      uTexture: { value: textureDOM },
      uMouse: { value: new THREE.Vector2(0, 0) },
      time: { value: 0.0 },
      uResolution: {
        value: new THREE.Vector2(window.innerWidth, window.innerHeight),
      }, // Add the resolution uniform
    }),
    [textureDOM]
  );

  const mouseLerped = useRef({ x: 0, y: 0 });

  useFrame((state, delta) => {
    const mouse = state.mouse;
    mouseLerped.current.x = THREE.MathUtils.lerp(
      mouseLerped.current.x,
      mouse.x,
      0.1
    );
    mouseLerped.current.y = THREE.MathUtils.lerp(
      mouseLerped.current.y,
      mouse.y,
      0.1
    );
    materialRef.current.uniforms.uMouse.value.x = mouseLerped.current.x;
    materialRef.current.uniforms.uMouse.value.y = mouseLerped.current.y;

    materialRef.current.uniforms.time.value += 0.5 * delta;
    materialRef.current.uniforms.uResolution.value.set(
      window.innerWidth,
      window.innerHeight
    );
  });

  const { viewport } = useThree();

  const shaders = [
    bgInteractive,
    // bgShader,
    lineShader,
    shaderOnText,
  ];

  return (
    <>
      <Html zIndexRange={[-1, -10]} prepend fullscreen>
        <div ref={(el) => setDomEl(el)} className="dom-element flex flex-col items-center justify-center gap-2">
          <div className="flex flex-col gap-4 lg:gap-10 text-center w-full font-raleway text-white pointer-events-none ">
            <p className="font-thin lg:text-[84px] text-[36px]">
              Hey,
            </p>
            <p className="lg:text-[150px] text-[56px] font-thin">
                I'm <span className="font-bold">Ram</span>
              </p>
            <p className="font-thin lg:text-[84px] text-[36px] lg:leading-[48px] leading-[24px]">
              <span className="font-thin not-italic"></span> a Creative
              Developer
            </p>
          </div>
          {/* <div className="flex w-full h-[50dvh] font-raleway font-extrabold">
            <div className="flex flex-col text-left justify-start items-start text-[122px] leading-[122px] w-full pl-24">
              <p>Creative</p>
              <p>Developer</p>
            </div>
            <div className="flex flex-col text-right items-end justify-end text-[122px] leading-[122px] w-full pr-28">
              <p>Based in</p>
              <p>India</p>
            </div>
          </div> */}
        </div>
      </Html>
      <mesh>
        <planeGeometry args={[width, height, 254, 254]} />
        <CustomShaderMaterial
          ref={materialRef}
          baseMaterial={THREE.MeshStandardMaterial}
          vertexShader={vertexShader}
          fragmentShader={shaders[fragShader]}
          uniforms={uniforms}
          flatShading
          toneMapped={false}
          silent
        />
        <Lights />
      </mesh>
      <EffectComposer>
        <Bloom mipmapBlur intensity={0.1} />
        {/* <Scanline
          blendFunction={BlendFunction.OVERLAY} // blend mode
          density={0.9} // scanline density
        /> */}
        {/* <Noise
          premultiply // enables or disables noise premultiplication
          blendFunction={BlendFunction.ADD} // blend mode
        /> */}
        {/* <HueSaturation
          blendFunction={BlendFunction.NORMAL} // blend mode
          hue={0} // hue in radians
          saturation={0} // saturation in radians
        /> */}
      </EffectComposer>
    </>
  );
};

export default Scene;
